import {
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import {
  PhoneNumberUtil,
  CountryCodeToRegionCodeMap,
} from "google-libphonenumber";
import { FormHelperText } from "@material-ui/core";
import {
  defaultCountries,
  FlagEmoji,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import IntlMessages from "../../util/IntlMessages";

// Function to remove the country code from the phone number
const removeCountryCode = (phoneNumber, countryCode) => {
  if (phoneNumber && countryCode && phoneNumber.startsWith("+" + countryCode)) {
    // Add 1 to account for the "+" sign
    return phoneNumber.replace("+" + countryCode, "");
  } else {
    // Phone number doesn't start with the expected country code
    return phoneNumber;
  }
};

export const PhoneNumber = ({
  identifier,
  value,
  onChange,
  label,
  countryMapping,
  handleInvalidPhoneNumber,
  ...restProps
}) => {
  let countryName = "";
  let code = "";

  /**for new contact defult client country */
  countryName =
    countryMapping && countryMapping.iso2
      ? countryMapping.iso2.toLowerCase()
      : countryMapping?.short_code
      ? countryMapping.short_code.toLowerCase()
      : "";

  const [initValue, setInitValue] = useState(value);
  const [country, setCountry] = useState(countryName);

  const selectRef = useRef();
  const inputRef = useRef();

  const [helperText, setHelperText] = useState("");

  const phoneUtil = PhoneNumberUtil.getInstance();

  const countries = defaultCountries.filter((country) => {
    const { iso2 } = parseCountry(country);
    return [countryName].includes(iso2);
  });

  const isPhoneValid = (phone) => {
    const regex = /^\d+$/;
    try {
      /**check If phoneNumber is a number */
      if (!regex.test(phone)) return false;

      /**check the same number if valid according to the country code */
      return phoneUtil.isValidNumber(
        phoneUtil.parseAndKeepRawInput(phone, country)
      );
    } catch (error) {
      return false;
    }
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    setInitValue(val);
    parsePhoneNumber(val);
  };

  const parsePhoneNumber = (val) => {
    try {
      if (val.startsWith("+")) {
        // Parse the phone number
        const parsedNumber = phoneUtil.parse(val, "");

        // Get the country code from the parsed number
        code = parsedNumber.getCountryCode();

        // Get the country based on the country code
        countryName = phoneUtil.getRegionCodeForCountryCode(code).toLowerCase();

        setTimeout(() => {
          if (countryName !== country) {
            setCountry(countryName);
          }

          const parsedValue = removeCountryCode(val, code);
          setInitValue(parsedValue);

          onChange(`+${code}${parsedValue}`);
        }, 500);
      } else {
        const parsedValue = phoneUtil.parse(val, country).getCountryCode();
        const cc = defaultCountries.find((c) => c[2] === `${parsedValue}`);

        if (cc && cc[2]) {
          onChange(`+${cc[2]}${val}`);
        } else {
          onChange(val);
        }
      }
    } catch (e) {
      onChange(val);
    }
  };

  useEffect(() => {
    try {
      if (initValue !== value && initValue === "") {
        if (value && value.startsWith("+")) {
          // Parse the phone number
          const parsedNumber = phoneUtil.parse(value, "");

          // Get the country code from the parsed number
          code = parsedNumber.getCountryCode();

          // Get the country based on the country code
          countryName = phoneUtil
            .getRegionCodeForCountryCode(code)
            .toLowerCase();
        } else {
          /**get country name for dropdown */
          countryName =
            countryMapping && countryMapping.iso2
              ? countryMapping.iso2.toLowerCase()
              : countryMapping?.short_code
              ? countryMapping.short_code.toLowerCase()
              : "";

          /**get countrycode to exclude from number*/
          code = countryMapping?.country_code
            ? countryMapping.country_code
            : countryMapping?.dialCode
            ? countryMapping.dialCode
            : "";
        }

        setCountry(countryName);

        /**exclude the country code */
        const parsedValue = removeCountryCode(value, code);
        setInitValue(parsedValue);
      }
    } catch (ex) {}
  }, [value, countryMapping]);

  useEffect(() => {
    if (initValue && !isPhoneValid(initValue)) {
      if (handleInvalidPhoneNumber) handleInvalidPhoneNumber(identifier, true);
      setHelperText("DIC_KEY_PHONE_NUMBER_VALIDATION");
    } else {
      if (handleInvalidPhoneNumber) handleInvalidPhoneNumber(identifier, false);
      setHelperText("");
    }
  }, [initValue]);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html:
            ".react-international-phone-country-selector{position:relative}.react-international-phone-country-selector-button{display:flex;height:var(--react-international-phone-height, 36px);box-sizing:border-box;align-items:center;justify-content:center;padding:0;border:1px solid var(--react-international-phone-country-selector-border-color, var(--react-international-phone-border-color, gainsboro));margin:0;appearance:button;-webkit-appearance:button;background-color:var(--react-international-phone-country-selector-background-color, var(--react-international-phone-background-color, white));cursor:pointer;text-transform:none;user-select:none}.react-international-phone-country-selector-button:hover{background-color:var(--react-international-phone-country-selector-background-color-hover, whitesmoke)}.react-international-phone-country-selector-button--hide-dropdown{cursor:auto}.react-international-phone-country-selector-button--hide-dropdown:hover{background-color:transparent}.react-international-phone-country-selector-button__button-content{display:flex;align-items:center;justify-content:center}.react-international-phone-country-selector-button__flag-emoji{margin:0 4px}.react-international-phone-country-selector-button__flag-emoji--disabled{opacity:.75}.react-international-phone-country-selector-button__dropdown-arrow{border-top:var(--react-international-phone-country-selector-arrow-size, 4px) solid var(--react-international-phone-country-selector-arrow-color, #777);border-right:var(--react-international-phone-country-selector-arrow-size, 4px) solid transparent;border-left:var(--react-international-phone-country-selector-arrow-size, 4px) solid transparent;margin-right:4px;transition:all .1s ease-out}.react-international-phone-country-selector-button__dropdown-arrow--active{transform:rotateX(180deg)}.react-international-phone-country-selector-button__dropdown-arrow--disabled{border-top-color:var(--react-international-phone-disabled-country-selector-arrow-color, #999)}.react-international-phone-country-selector-button--disabled{background-color:var(--react-international-phone-disabled-country-selector-background-color, var(--react-international-phone-disabled-background-color, whitesmoke));cursor:auto}.react-international-phone-country-selector-button--disabled:hover{background-color:var(--react-international-phone-disabled-country-selector-background-color, var(--react-international-phone-disabled-background-color, whitesmoke))}.react-international-phone-flag-emoji{width:var(--react-international-phone-flag-width, 24px);height:var(--react-international-phone-flag-height, 24px);box-sizing:border-box}.react-international-phone-country-selector-dropdown{position:absolute;z-index:1;top:var(--react-international-phone-dropdown-left, 44px);left:var(--react-international-phone-dropdown-left, 0);display:flex;width:300px;max-height:200px;flex-direction:column;padding:4px 0;margin:0;background-color:var(--react-international-phone-dropdown-item-background-color, var(--react-international-phone-background-color, white));box-shadow:var(--react-international-phone-dropdown-shadow, 2px 2px 16px rgba(0, 0, 0, .25));color:var(--react-international-phone-dropdown-item-text-color, var(--react-international-phone-text-color, #222));list-style:none;overflow-y:scroll}.react-international-phone-country-selector-dropdown__list-item{display:flex;min-height:var(--react-international-phone-dropdown-item-height, 28px);box-sizing:border-box;align-items:center;padding:2px 8px}.react-international-phone-country-selector-dropdown__list-item-flag-emoji{margin-right:8px}.react-international-phone-country-selector-dropdown__list-item-country-name{margin-right:8px;font-size:var(--react-international-phone-dropdown-item-font-size, 14px)}.react-international-phone-country-selector-dropdown__list-item-dial-code{color:var(--react-international-phone-dropdown-item-dial-code-color, gray);font-size:var(--react-international-phone-dropdown-item-font-size, 14px)}.react-international-phone-country-selector-dropdown__list-item:hover{background-color:var(--react-international-phone-selected-dropdown-item-background-color, var(--react-international-phone-selected-dropdown-item-background-color, whitesmoke));cursor:pointer}.react-international-phone-country-selector-dropdown__list-item--selected,.react-international-phone-country-selector-dropdown__list-item--focused{background-color:var(--react-international-phone-selected-dropdown-item-background-color, whitesmoke);color:var(--react-international-phone-selected-dropdown-item-text-color, var(--react-international-phone-text-color, #222))}.react-international-phone-country-selector-dropdown__list-item--selected .react-international-phone-country-selector-dropdown__list-item-dial-code,.react-international-phone-country-selector-dropdown__list-item--focused .react-international-phone-country-selector-dropdown__list-item-dial-code{color:var(--react-international-phone-selected-dropdown-item-dial-code-color, var(--react-international-phone-dropdown-item-dial-code-color, gray))}.react-international-phone-country-selector-dropdown__list-item--focused{background-color:var(--react-international-phone-selected-dropdown-item-background-color, var(--react-international-phone-selected-dropdown-item-background-color, whitesmoke))}.react-international-phone-dial-code-preview{display:flex;align-items:center;justify-content:center;padding:0 8px;border:1px solid var(--react-international-phone-dial-code-preview-border-color, var(--react-international-phone-border-color, gainsboro));margin-right:-1px;background-color:var(--react-international-phone-dial-code-preview-background-color, var(--react-international-phone-background-color, white));color:var(--react-international-phone-dial-code-preview-text-color, var(--react-international-phone-text-color, #222));font-size:var(--react-international-phone-dial-code-preview-font-size, var(--react-international-phone-font-size, 13px))}.react-international-phone-dial-code-preview--disabled{background-color:var(--react-international-phone-dial-code-preview-disabled-background-color, var(--react-international-phone-disabled-background-color, whitesmoke));color:var(--react-international-phone-dial-code-preview-disabled-text-color, var(--react-international-phone-disabled-text-color, #666))}.react-international-phone-input-container{display:flex}.react-international-phone-input-container .react-international-phone-country-selector-button{border-radius:var(--react-international-phone-border-radius, 4px);margin-right:-1px;border-bottom-right-radius:0;border-top-right-radius:0}.react-international-phone-input-container .react-international-phone-input{overflow:visible;height:var(--react-international-phone-height, 36px);box-sizing:border-box;padding:0 8px;border:1px solid var(--react-international-phone-border-color, gainsboro);border-radius:var(--react-international-phone-border-radius, 4px);margin:0;background-color:var(--react-international-phone-background-color, white);border-bottom-left-radius:0;border-top-left-radius:0;color:var(--react-international-phone-text-color, #222);font-family:inherit;font-size:var(--react-international-phone-font-size, 13px)}.react-international-phone-input-container .react-international-phone-input:focus{outline:none}.react-international-phone-input-container .react-international-phone-input--disabled{background-color:var(--react-international-phone-disabled-background-color, whitesmoke);color:var(--react-international-phone-disabled-text-color, #666)}",
        }}
      />
      <TextField
        fullWidth
        variant="outlined"
        label={label}
        color="primary"
        placeholder="Phone number"
        value={initValue}
        onChange={handlePhoneChange}
        onPaste={handlePhoneChange}
        error={helperText !== ""}
        helperText={
          helperText && (
            <span style={{ color: "#d32f2f" }}>
              <IntlMessages id={helperText} />
            </span>
          )
        }
        type="tel"
        inputRef={inputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              style={{ marginRight: "2px", marginLeft: "-8px" }}
            >
              <Select
                ref={selectRef}
                MenuProps={{
                  style: {
                    height: "300px",
                    width: "360px",
                    top: "10px",
                    left: "-34px",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
                sx={{
                  width: "max-content",
                  // Remove default outline (display only on focus)
                  fieldset: {
                    display: "none",
                  },
                  '&.Mui-focused:has(div[aria-expanded="false"])': {
                    fieldset: {
                      display: "block",
                    },
                  },
                  // Update default spacing
                  ".MuiSelect-select": {
                    padding: "8px",
                    paddingRight: "24px !important",
                  },
                  svg: {
                    right: 0,
                  },
                }}
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                }}
                renderValue={(value) => (
                  <FlagEmoji iso2={value} style={{ display: "flex" }} />
                )}
              >
                {defaultCountries.map((c) => {
                  const country = parseCountry(c);
                  return (
                    <MenuItem key={country.iso2} value={country.iso2}>
                      <FlagEmoji
                        iso2={country.iso2}
                        style={{ marginRight: "8px" }}
                      />
                      <Typography marginRight="8px">{country.name}</Typography>
                      <Typography color="gray">+{country.dialCode}</Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </InputAdornment>
          ),
        }}
        {...restProps}
      />
    </>
  );
};

export default PhoneNumber;
